import { FC, useEffect, useState } from "react";
import { Autocomplete, Chip, TextField } from "@mui/material";

import { useAppDispatch } from "../../store/hooks";
import { fetchTags } from "../../store/tagStore/tagThunk";
import { ITag, TagAppliesTo } from "../../utils/tag.types";

interface TagSelectProps {
  /** Currently selected tag ids. */
  value?: string[];
  onChange: (tagIds: string[]) => void;
  disabled?: boolean;
}

/**
 * Invoice tag picker. Offers active tags from the account's Tag master whose
 * `appliesTo` includes INVOICE (the API also returns generic tags). Options are
 * kept in local state so this does not disturb the tags slice used by the
 * Tags master screen in Settings.
 */
const TagSelect: FC<TagSelectProps> = ({ value, onChange, disabled }) => {
  const dispatch = useAppDispatch();

  const [options, setOptions] = useState<ITag[]>([]);

  useEffect(() => {
    const loadTags = async () => {
      try {
        const data: any = await dispatch(
          fetchTags(`appliesTo=${TagAppliesTo.INVOICE}&status=active`)
        ).unwrap();

        setOptions(data || []);
      } catch (error) {
        console.error('Error fetching invoice tags:', error);
      }
    };

    loadTags();
  }, [dispatch]);

  // `value` holds ids; map them back onto the loaded options for display.
  const selected = options.filter((option) => (value || []).includes(option._id));

  return (
    <>
      <h5 className='c-label'>Tags</h5>
      <Autocomplete
        multiple
        size='small'
        disabled={disabled}
        options={options}
        value={selected}
        onChange={(event, newValue) => onChange(newValue.map((tag) => tag._id))}
        getOptionLabel={(option: ITag) => option.name}
        isOptionEqualToValue={(option: ITag, val: ITag) => option._id === val._id}
        ChipProps={{ size: 'small' }}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option: ITag, index: number) => (
            <Chip
              {...getTagProps({ index })}
              key={option._id}
              label={option.name}
              size='small'
              style={{
                backgroundColor: option.color || '#7f8c9b',
                color: '#fff',
                height: 22,
                fontSize: 12,
              }}
            />
          ))
        }
        renderOption={(props, option: ITag) => (
          <li {...props} key={option._id} style={{ fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              width: 10, height: 10, borderRadius: '50%',
              backgroundColor: option.color || '#7f8c9b',
              display: 'inline-block', flexShrink: 0
            }} />
            {option.name}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            className='c-text-field'
            variant="standard"
            placeholder={selected.length ? '' : 'Select Tags'}
            InputProps={{ ...params.InputProps, disableUnderline: true }}
          />
        )}
        sx={{ width: '100%', marginBottom: '3px' }}
      />
    </>
  );
};

export default TagSelect;
